<script setup>
import { ref, computed, onMounted, onUnmounted, defineProps, defineEmits, defineExpose } from 'vue';
import { useModalBackdropClose } from '@/composables/useModalBackdropClose';

// Props
const props = defineProps({
  // Size constraints
  maxWidth: {
    type: Number,
    default: 300
  },
  maxHeight: {
    type: Number,
    default: 300
  },
  // Aspect ratio constraint (width/height ratio, e.g., 1 for square, 16/9 for widescreen)
  aspectRatio: {
    type: Number,
    default: null
  },
  // File constraints
  maxFileSize: {
    type: Number,
    default: 5 * 1024 * 1024 // 5MB default
  },
  acceptedTypes: {
    type: Array,
    default: () => ['image/jpeg', 'image/png', 'image/webp', 'image/gif']
  },
  // Initial image
  initialImage: {
    type: String,
    default: null
  },
  // UI customization
  borderColor: {
    type: String,
    default: 'border-gray-300'
  },
  backgroundColor: {
    type: String,
    default: 'bg-gray-50'
  }
});

  // Emits
  const emit = defineEmits(['close', 'image-selected', 'image-cropped', 'error']);

  const close = () => emit('close');
  const { onBackdropMousedown, onBackdropMouseup, onContentMousedown } = useModalBackdropClose(close);

// Reactive state
const fileInput = ref(null);
// const canvasContainer = ref(null);
const canvas = ref(null);
const ctx = ref(null);

const selectedFile = ref(null);
const imageObj = ref(null);
const isDragging = ref(false);
const isImageLoaded = ref(false);

// Image positioning and scaling
const scale = ref(1);
const minScale = ref(0.1);
const maxScale = ref(5);
const offsetX = ref(0);
const offsetY = ref(0);
const lastMouseX = ref(0);
const lastMouseY = ref(0);

// Computed properties
const containerWidth = computed(() => props.maxWidth);
const containerHeight = computed(() => {
  if (props.aspectRatio) {
    return props.maxWidth / props.aspectRatio;
  }
  return props.maxHeight;
});

const boundaryBox = computed(() => ({
  x: (containerWidth.value - props.maxWidth) / 2,
  y: (containerHeight.value - (props.aspectRatio ? containerHeight.value : props.maxHeight)) / 2,
  width: props.maxWidth,
  height: props.aspectRatio ? containerHeight.value : props.maxHeight
}));

// File size formatting
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// File validation
const validateFile = (file) => {
  if (!props.acceptedTypes.includes(file.type)) {
    emit('error', `File type ${file.type} is not supported. Accepted types: ${props.acceptedTypes.join(', ')}`);
    return false;
  }
  
  if (file.size > props.maxFileSize) {
    emit('error', `File size ${formatFileSize(file.size)} exceeds maximum allowed size of ${formatFileSize(props.maxFileSize)}`);
    return false;
  }
  
  return true;
};

// Image loading
const loadImage = (file) => {
  if (!validateFile(file)) return;
  
  selectedFile.value = file;
  const reader = new FileReader();
  
  reader.onload = (e) => {
    const img = new Image();
    img.onload = () => {
      imageObj.value = img;
      resetImagePosition();
      isImageLoaded.value = true;
      drawCanvas();
      emit('image-selected', file);
    };
    img.src = e.target.result;
  };
  
  reader.readAsDataURL(file);
};

// Load initial image if provided
const loadInitialImage = () => {
  if (props.initialImage) {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imageObj.value = img;
      resetImagePosition();
      isImageLoaded.value = true;
      drawCanvas();
    };
    img.src = props.initialImage;
  }
};

// Reset image position and scale
const resetImagePosition = () => {
  if (!imageObj.value) return;
  
  const img = imageObj.value;
  const containerAspect = containerWidth.value / containerHeight.value;
  const imageAspect = img.width / img.height;
  
  // Calculate scale to fit image in container
  if (imageAspect > containerAspect) {
    scale.value = containerWidth.value / img.width;
  } else {
    scale.value = containerHeight.value / img.height;
  }
  
  // Center the image
  offsetX.value = (containerWidth.value - img.width * scale.value) / 2;
  offsetY.value = (containerHeight.value - img.height * scale.value) / 2;
  
  // Set minimum scale to ensure image can cover the boundary box
  const minScaleX = boundaryBox.value.width / img.width;
  const minScaleY = boundaryBox.value.height / img.height;
  minScale.value = Math.max(minScaleX, minScaleY) * 0.5; // Allow some zoom out
  
  // Redraw the canvas to show the reset position immediately
  drawCanvas();
};

// Canvas drawing
const drawCanvas = () => {
  if (!ctx.value || !imageObj.value) return;
  
  const canvasEl = canvas.value;
  canvasEl.width = containerWidth.value;
  canvasEl.height = containerHeight.value;
  
  // Clear canvas
  ctx.value.clearRect(0, 0, canvasEl.width, canvasEl.height);
  
  // Draw image
  ctx.value.drawImage(
    imageObj.value,
    offsetX.value,
    offsetY.value,
    imageObj.value.width * scale.value,
    imageObj.value.height * scale.value
  );
  
  // Draw boundary box overlay
  ctx.value.save();
  
  // Darken area outside boundary box
  ctx.value.fillStyle = 'rgba(0, 0, 0, 0.4)';
  ctx.value.fillRect(0, 0, canvasEl.width, canvasEl.height);
  
  // Clear the boundary box area
  ctx.value.globalCompositeOperation = 'destination-out';
  ctx.value.fillRect(
    boundaryBox.value.x,
    boundaryBox.value.y,
    boundaryBox.value.width,
    boundaryBox.value.height
  );
  
  ctx.value.restore();
  
  // Draw boundary box border
  ctx.value.strokeStyle = '#3b82f6';
  ctx.value.lineWidth = 2;
  ctx.value.setLineDash([5, 5]);
  ctx.value.strokeRect(
    boundaryBox.value.x,
    boundaryBox.value.y,
    boundaryBox.value.width,
    boundaryBox.value.height
  );
  ctx.value.setLineDash([]);
};

// Mouse/touch event handlers
const startDrag = (e) => {
  if (!isImageLoaded.value) return;
  
  isDragging.value = true;
  const rect = canvas.value.getBoundingClientRect();
  const clientX = e.clientX || (e.touches && e.touches[0].clientX);
  const clientY = e.clientY || (e.touches && e.touches[0].clientY);
  
  lastMouseX.value = clientX - rect.left;
  lastMouseY.value = clientY - rect.top;
  
  e.preventDefault();
};

const drag = (e) => {
  if (!isDragging.value || !isImageLoaded.value) return;
  
  const rect = canvas.value.getBoundingClientRect();
  const clientX = e.clientX || (e.touches && e.touches[0].clientX);
  const clientY = e.clientY || (e.touches && e.touches[0].clientY);
  
  const mouseX = clientX - rect.left;
  const mouseY = clientY - rect.top;
  
  const deltaX = mouseX - lastMouseX.value;
  const deltaY = mouseY - lastMouseY.value;
  
  offsetX.value += deltaX;
  offsetY.value += deltaY;
  
  lastMouseX.value = mouseX;
  lastMouseY.value = mouseY;
  
  drawCanvas();
  e.preventDefault();
};

const endDrag = () => {
  isDragging.value = false;
};

// Zoom functionality
const zoom = (delta, centerX = null, centerY = null) => {
  if (!isImageLoaded.value) return;
  
  const zoomFactor = delta > 0 ? 1.1 : 0.9;
  const newScale = Math.min(Math.max(scale.value * zoomFactor, minScale.value), maxScale.value);
  
  if (newScale !== scale.value) {
    // If center point provided, zoom towards that point
    if (centerX !== null && centerY !== null) {
      const scaleDiff = newScale - scale.value;
      offsetX.value -= (centerX - offsetX.value) * (scaleDiff / scale.value);
      offsetY.value -= (centerY - offsetY.value) * (scaleDiff / scale.value);
    }
    
    scale.value = newScale;
    drawCanvas();
  }
};

const handleWheel = (e) => {
  e.preventDefault();
  const rect = canvas.value.getBoundingClientRect();
  const centerX = e.clientX - rect.left;
  const centerY = e.clientY - rect.top;
  zoom(-e.deltaY, centerX, centerY);
};

// File input handlers
const triggerFileInput = () => {
  fileInput.value?.click();
};

const handleFileSelect = (e) => {
  const file = e.target.files[0];
  if (file) {
    loadImage(file);
  }
};

const handleDrop = (e) => {
  e.preventDefault();
  const file = e.dataTransfer.files[0];
  if (file) {
    loadImage(file);
  }
};

const handleDragOver = (e) => {
  e.preventDefault();
};

// Get cropped image as File object
const getCroppedImage = () => {
  if (!isImageLoaded.value) return null;
  
  // Create a new canvas for the cropped image
  const cropCanvas = document.createElement('canvas');
  const cropCtx = cropCanvas.getContext('2d');
  
  cropCanvas.width = boundaryBox.value.width;
  cropCanvas.height = boundaryBox.value.height;
  
  // Calculate source coordinates on the original image
  const sourceX = (boundaryBox.value.x - offsetX.value) / scale.value;
  const sourceY = (boundaryBox.value.y - offsetY.value) / scale.value;
  const sourceWidth = boundaryBox.value.width / scale.value;
  const sourceHeight = boundaryBox.value.height / scale.value;
  
  // Draw the cropped portion
  cropCtx.drawImage(
    imageObj.value,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    0,
    0,
    boundaryBox.value.width,
    boundaryBox.value.height
  );
  
  return new Promise((resolve) => {
    cropCanvas.toBlob((blob) => {
      if (blob) {
        // Create a File object from the blob
        const fileName = selectedFile.value ? selectedFile.value.name.replace(/\.[^/.]+$/, '_cropped.png') : 'cropped_image.png';
        const file = new File([blob], fileName, { type: 'image/png' });
        resolve(file);
      } else {
        resolve(null);
      }
    }, 'image/png', 0.9);
  });
};

// Export cropped image
const exportCroppedImage = async () => {
  const croppedFile = await getCroppedImage();
  if (croppedFile) {
    emit('image-cropped', croppedFile);
  }
};

// Component lifecycle
onMounted(() => {
  if (canvas.value) {
    ctx.value = canvas.value.getContext('2d');
  }
  loadInitialImage();
  
  // Add global event listeners for drag end
  document.addEventListener('mouseup', endDrag);
  document.addEventListener('mousemove', drag);
  document.addEventListener('touchend', endDrag);
  document.addEventListener('touchmove', drag);
});

onUnmounted(() => {
  document.removeEventListener('mouseup', endDrag);
  document.removeEventListener('mousemove', drag);
  document.removeEventListener('touchend', endDrag);
  document.removeEventListener('touchmove', drag);
});

// Expose methods for parent component
defineExpose({
  getCroppedImage,
  exportCroppedImage,
  resetImagePosition,
  loadImage
});
</script>

<template>
    <div class="fixed top-0 left-0 right-0 bottom-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50" @mousedown.self="onBackdropMousedown" @mouseup.self="onBackdropMouseup">
        <div class="ui-modal p-8 rounded-xl max-h-[calc(100vh-50px)] overflow-y-auto shadow-mc-lg" @mousedown="onContentMousedown">
            <div class="touch-none w-full flex flex-col items-center justify-center gap-4">

                <!-- File input (hidden) -->
                <input 
                ref="fileInput"
                type="file" 
                :accept="acceptedTypes.join(',')"
                @change="handleFileSelect"
                class="hidden"
                />
                
                <!-- Main container -->
                <div 
                class="relative border-2 border-dashed rounded-lg overflow-hidden"
                :class="[borderColor, backgroundColor]"
                :style="{ width: containerWidth + 'px', height: containerHeight + 'px' }"
                @drop="handleDrop"
                @dragover="handleDragOver"
                >
                    <!-- Canvas for image display -->
                    <canvas
                        ref="canvas"
                        class="absolute inset-0 cursor-move"
                        :class="{ 'cursor-grab': !isDragging, 'cursor-grabbing': isDragging }"
                        @mousedown="startDrag"
                        @touchstart="startDrag"
                        @wheel="handleWheel"
                    />
                    
                    <!-- Upload prompt (shown when no image) -->
                    <div
                        v-if="!isImageLoaded"
                        class="absolute inset-0 flex flex-col items-center justify-center text-ink-muted dark:text-on-dark-muted p-4"
                    >
                        <svg class="w-12 h-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
                        </svg>
                        <p class="text-sm text-center mb-2">
                        Drop an image here or 
                        <button 
                            @click="triggerFileInput"
                            class="text-blue-600 hover:text-blue-800 underline"
                        >
                            browse files
                        </button>
                        </p>
                        <p class="text-xs text-ink-muted dark:text-on-dark-muted text-center">
                        Max size: {{ formatFileSize(maxFileSize) }}<br>
                        Target size: {{ maxWidth }}×{{ props.aspectRatio ? containerHeight : maxHeight }}px
                        </p>
                    </div>
                    
                    <!-- Size limit indicator overlay -->
                    <div 
                        v-if="isImageLoaded"
                        class="absolute pointer-events-none"
                        :style="{
                        left: boundaryBox.x + 'px',
                        top: boundaryBox.y + 'px',
                        width: boundaryBox.width + 'px',
                        height: boundaryBox.height + 'px'
                        }"
                    >
                        <div class="absolute -top-6 left-0 text-xs bg-blue-600 text-white px-2 py-1 rounded">
                        {{ boundaryBox.width }}×{{ boundaryBox.height }}px
                        </div>
                    </div>
                </div>
            
                <!-- Controls -->
                <div v-if="isImageLoaded" class="w-full flex flex-col gap-4 items-center">
                    <!-- File info -->
                    <div v-if="selectedFile" class="w-full text-center text-xs text-ink-muted dark:text-on-dark-muted flex flex-row gap-2 justify-center items-center mb-4">
                        <button 
                            @click="triggerFileInput"
                            class="default-button"
                        >
                            Change Image
                        </button>
                        <p>{{ selectedFile.name }} ({{ formatFileSize(selectedFile.size) }})</p>
                    </div>

                    <!-- Zoom controls -->
                    <div class="flex items-center gap-2">
                        <span class="text-sm text-ink-muted dark:text-on-dark-muted">Zoom:</span>
                        <button
                        @click="zoom(-1)"
                        class="px-2 py-1 bg-mc-steel/40 hover:bg-mc-steel dark:bg-white/10 dark:hover:bg-white/20 rounded-mc-sm text-sm text-mc-ink dark:text-on-dark transition-colors duration-160"
                        :disabled="scale <= minScale"
                        >
                        −
                        </button>
                        <span class="text-sm min-w-12 text-center">{{ Math.round(scale * 100) }}%</span>
                        <button
                        @click="zoom(1)"
                        class="px-2 py-1 bg-mc-steel/40 hover:bg-mc-steel dark:bg-white/10 dark:hover:bg-white/20 rounded-mc-sm text-sm text-mc-ink dark:text-on-dark transition-colors duration-160"
                        :disabled="scale >= maxScale"
                        >
                        +
                        </button>
                    </div>
                
                    <!-- Action buttons -->
                    <div class="flex gap-2 justify-center flex-wrap">
                        <button
                        @click="resetImagePosition"
                        class="px-3 py-1 bg-ink-muted dark:bg-white/20 hover:bg-mc-ink dark:hover:bg-white/30 text-white rounded-mc-sm text-sm transition-colors duration-160"
                        >
                        Reset
                        </button>
                        <button 
                        @click="exportCroppedImage"
                        class="px-3 py-1 bg-green-500 hover:bg-green-600 text-white rounded text-sm"
                        >
                        Apply Crop
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>